import React from 'react'
import { Link } from 'react-router-dom'
import { useSEO } from '../hooks/useSEO'
import { useCMSSide } from '../hooks/useCMS'

export function AlignFootwearPage() {
  const { side: cmsSide } = useCMSSide('align-footwear-ny')

  useSEO({
    title: cmsSide?.seo_titel || 'ALIGN FOOTWEAR | Trekantens Kropsterapi',
    description: cmsSide?.seo_beskrivelse || 'Ca. 80 % af alle mennesker går skævt på fødderne på den ene eller den anden måde - det giver en skæv kropsholdning. Læs om Align Footwear indlægssåler.',
    ogImage: cmsSide?.dele_billede?.[0] || 'https://assets.auxodev.dk/assets/2a253654-8b0d-44c3-813e-14453c64ff7c/fod-sa-l-tdmuwazn1ecgual-djbocq-w1024.jpg',
  })

  return (
    <div className="bg-[#FAF8F5]">
      {/* Informative Notice Banner */}
      <div className="bg-[#1b3a4b]/8 border-b border-[#1b3a4b]/15 py-3 px-4 text-center text-xs text-[#1b3a4b]">
        <span>
          Bemærk: Bo Steendahl fokuserer i dag udelukkende på behandlingsforløb inden for <strong>Kropsterapi, Coaching og Hypnoterapi</strong>.{' '}
          <Link to="/" className="underline font-semibold hover:text-[#7c875a]">
            Gå til forsiden
          </Link>
        </span>
      </div>

      {/* Hero Section */}
      <section className="relative py-16 lg:py-20 bg-gradient-to-b from-[#F5EFE9] to-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="font-serif text-[32px] sm:text-5xl font-bold text-[#1b3a4b] tracking-tight">
            ALIGN FOOTWEAR
          </h1>
          <p className="mt-3 text-base sm:text-lg text-[#64748B] max-w-xl mx-auto">
            Arkivside bevaret for eksisterende links og SEO
          </p>
        </div>
      </section>

      {/* Main Scraped Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-sm border border-[#E5E0D8] space-y-8 text-sm sm:text-base text-[#475569] leading-relaxed">
            <p className="font-medium text-[#1b3a4b] text-base">
              Ca. 80 % af alle mennesker går skævt på fødderne på den ene eller den anden måde - det giver en skæv kropsholdning.
            </p>

            <div className="space-y-4">
              <h2 className="font-serif text-2xl font-bold text-[#1b3a4b]">
                Har du smerter i fødder, knæ, hofter, ryg eller nakke?
              </h2>
              <p>
                Align Footwear indlægssåler skaber, ved hjælp af tre støttepunkter en lige fodstilling – det vil sige, at risikoen for at gå skævt på fødderne reduceres. Det er det, der også kaldes supination og pronation. At rette op på hæl og ankel, så de står lige, vil bidrage til, at også knæ, hofte og skuldre rettes op, dvs. du får en mere lige kropsholdning. Når du er rettet op til en sund og lige kropsposition, vil din kropsvægt fordeles optimalt, således, at unødige pres, vrid og stød undgås. Samtidig er det godt for mange smerteudfordringer i fødderne: nedsunken forfod, platfod, hælspore, hulfod/ høj svang, platfod. Mange smerter opstår, fordi du automatisk vil forsøge at aflaste udfordringer i fødderne – og derfor trækker man skævt op igennem kroppen. Det kan betyde knæsmerter, lænde- og rygproblemer, spændinger i skuldrene osv. Når du ved hjælp af indlægssålerne fra Align Footwear bliver rettet op i fødder og ankler, vil dette have en positiv effekt for mange udfordringer, du kan opleve op igennem kroppen. Filosofien bag Align Footwear er, modsat den indtil nu mere traditionelle fremgangsmåde, at undgå at fastlåse foden ved hjælp af svangstøtte, pelotter og kiler. Det fjerner bevægeligheden - og det gør fodmuskulaturen slap - og det kan både blive smertefuldt i længden og være svært at genoptræne.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-6 p-6 rounded-2xl bg-[#FAF8F5] border border-[#E5E0D8]">
              <img
                src="/_assets/fod-sa-l-tdmuwazn1ecgual-djbocq-w1024.jpg"
                alt="Align Footwear indlægssål"
                className="w-48 h-auto rounded-xl object-cover shadow"
              />
              <div className="space-y-2">
                <span className="font-serif text-3xl font-bold text-[#1b3a4b]">499,-</span>
                <span className="block text-xs font-semibold text-[#7c875a] uppercase">60 dages returret</span>
                <p className="text-xs text-[#64748B]">
                  Problemer med fødderne kan skyldes enten skæv kropsholdning eller forkert fodtøj.
                </p>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-[#E5E0D8]">
              <p>
                Desuden kan medfødte misdannelser og helbredsmæssige tilstande såsom gigt og diabetes resultere i problemer med foden. Foden består af 26 knogler, 33 led og mere end 100 ledbånd (ligamenter) samt flere forskellige muskler, fedtpuder, sener og nerver.
              </p>
              <p>
                Eftersom fødderne bærer hele kroppens vægt igennem vores liv, er der også mange forskellige årsager til, at der kan opstå problemer på et tidspunkt.
              </p>
              <p className="font-medium text-[#1b3a4b]">Føddernes rolle er bl.a. at:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Bære kropsvægten</li>
                <li>Afdæmpe stød og vrid</li>
                <li>Opretholde kroppens balance</li>
                <li>Gøre at vi kan gå, løbe, springe og derved ”skubbe” kroppen fremad</li>
              </ul>
            </div>

            <div className="space-y-3 pt-4 border-t border-[#E5E0D8]">
              <h3 className="font-serif text-xl font-bold text-[#1b3a4b]">
                Bestil dine Align Footwear her
              </h3>
              <p className="text-sm">
                Indlægssålerne kan afhentes efter forudgående aftale på{' '}
                <a
                  href="https://goo.gl/maps/TQfVJdsKgVqRkDXE9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-[#1b3a4b] underline"
                >
                  Vendersgade 37, 7000 Fredericia
                </a>{' '}
                eller{' '}
                <a
                  href="https://goo.gl/maps/yZG7566JwDTeE8Zo8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-[#1b3a4b] underline"
                >
                  Randsborgvej 3b, Høll, 7080 Børkop
                </a>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
